import { IProductData, normalizeDisplaySet } from '@/utils';
import { DateTime } from 'luxon';
import { ProductData } from '../hooks/types';

// Example: 1713216693  -> April 15, 2024, at 21:31:33
export function convertUnixTimestampToUTCDate(timestamp: number) {
  // Convert Unix timestamp to milliseconds by multiplying by 1000
  const date = new Date(timestamp * 1000);
  return date.toString();
}

// Example: "Mon Apr 15 2024 17:43:25 GMT-0700 (Pacific Daylight Time)" -> "2024-04-16T00:43:25.000Z"
export function convertDateStringToPostgresqlTimestamp(dateString: string) {
  const date = new Date(dateString);
  return date.toISOString();
}

// Example: 1713216693 -> "2024-04-16T00:43:25.000Z"
export function convertUnixTimestampToISOString(timestamp: number) {
  const date = new Date(timestamp * 1000);
  return date.toISOString();
}

export function getCurrentTimeInISOFormat() {
  return new Date().toISOString();
}

// Outputs: "May 10, 2024" (if today is May 10, 2024)
export function getCurrentDayInLocaleDateString() {
  const date = new Date();
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return formattedDate;
}

// Output Example: 2024-06-07T13:01:00-07:00
export function getCurrentDateInPST() {
  const pstDate = DateTime.now().setZone('America/Los_Angeles').toISO();
  return pstDate;
}

/**
 *
 * @param isoDateString "2024-06-07T13:01:00-07:00"
 * @returns string "06/07/2024"
 */
export function formatISODate(isoDateString: string): string {
  const date = new Date(isoDateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid ISO date string');
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

/**
 * Need this one because without a timezone, it'll assume UTC midnight. This will cause the date to shift.
 * @param isoDateString "2024-06-07"
 * @returns string "06/07/2024"
 */
export function formatISODateNoTimeZone(isoDateString: string): string {
  if (!isoDateString) return;

  const [year, month, day] = isoDateString.split('-');

  if (!year || !month || !day) {
    throw new Error('Invalid ISO date string');
  }

  return `${month}/${day}/${year}`;
}

/**
 * Calculates value of weeks from current date.
 * Note: Only returns the number. Don't forget to add "weeks" after
 * @param targetDate
 * @returns
 */
export function weeksFromCurrentDate(targetDate: string): number {
  const currentDate = new Date();
  const target = new Date(targetDate);

  const diffInMilliseconds = target.getTime() - currentDate.getTime();
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  const diffInWeeks = diffInDays / 7;

  return Math.ceil(diffInWeeks);
}

export function getCurrentMonth() {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const date = new Date();
  return monthNames[date.getMonth()];
}

export function calculateTimeTo2PM() {
  const now = new Date();
  const target = new Date();

  target.setHours(14); // Set to 2 PM local time
  target.setMinutes(0);
  target.setSeconds(0);
  target.setMilliseconds(0);

  if (now.getHours() >= 14) {
    // Adjust for PST timezone offset
    target.setDate(target.getDate() + 1); // Set to next day if past 2 PM
  }

  const diff: number = target.getTime() - now.getTime();

  // Convert milliseconds to hours and minutes
  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor((diff / 1000 / 60) % 60);

  if (hours < 0 || minutes < 0) {
    return ''; // In case of negative values
  }

  return `${hours} hours ${minutes} mins`;
}

// NTS: Turn this into a more re-usable function later
// Was causing multiple re-renders when I was trying to turn it into one, probably a different issue though.
const DEFAULT_TIMEZONE = 'America/Los_Angeles';

export function calculateTimeTo11AM(warehouseTimezone: string = DEFAULT_TIMEZONE) {
  // Get current warehouse time
  const warehouseNow = DateTime.now().setZone(warehouseTimezone);

  // Create target time (11 AM) in warehouse timezone
  let target = warehouseNow.set({
    hour: 11,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  // If current time is past 11 AM, set target to next day
  if (warehouseNow.hour >= 11) {
    target = target.plus({ days: 1 });
  }

  // Calculate difference
  const diff = target.diff(warehouseNow, ['hours', 'minutes']).toObject();

  const hours = Math.floor(diff.hours || 0);
  const minutes = Math.floor(diff.minutes || 0);

  if (hours < 0 || minutes < 0) {
    return '';
  }

  return hours === 0 ? `${minutes} mins` : `${hours} hours ${minutes} mins`;
}

export function cardIsExpired(expiryMonth: string, expiryYear: string): boolean {
  // Get the current date
  const currentDate = new Date();

  // Create a Date object for the card expiry (set to the last day of the expiry month)
  const expiryDate = new Date(parseInt(expiryYear), parseInt(expiryMonth) - 1 + 1, 0);

  // Check if the expiry date is before the current date
  return expiryDate < currentDate;
}

export function formatStringToDate(dateStr: string) {
  const formats = ['yyyy/MM/dd', 'yyyy/M/d', 'MM/dd/yyyy', 'M/d/yyyy', 'yyyy-MM-dd', 'dd-MM-yyyy'];

  for (const format of formats) {
    const dt = DateTime.fromFormat(dateStr, format);
    if (dt.isValid) {
      return dt.toJSDate(); // or dt if you want to keep it as a Luxon DateTime object
    }
  }

  // If no formats match, return an invalid DateTime
  return DateTime.invalid('Invalid date format');
}

export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

// Handling Shipping Date format as YYYY-MM-DD for both Stoq options. (fixed_date and 00_days_after)
export function formatShippingDate(shippingText: string): string | null {
  if (!shippingText) return null;

  // Try to parse formats like "30 Jun 2025"
  const exactDate = DateTime.fromFormat(shippingText.trim(), 'dd LLL yyyy', {
    zone: 'America/Los_Angeles',
  });

  if (exactDate.isValid) {
    return exactDate.toISODate(); // ➜ YYYY-MM-DD in PST/PDT
  }

  // Try to parse "50 days after checkout"
  const match = shippingText.match(/(\d+)\s+days\s+after\s+checkout/i);
  if (match) {
    const daysToAdd = parseInt(match[1], 10);
    const today = DateTime.now().setZone('America/Los_Angeles').startOf('day');

    const shippingDate = today.plus({ days: daysToAdd });

    return shippingDate.toISODate(); // YYYY-MM-DD
  }

  // If nothing matches, return null
  return null;
}

export function formatPreorderDateFromPreorderName(datePart: string): string | null {
  // Match format like "16wk" or "20wks"
  const match = datePart.match(/^(\d+)\s*wks?$/i);
  if (match) {
    const daysToAdd = parseInt(match[1], 10) * 7; // convert weeks to days
    const today = DateTime.now().setZone('America/Los_Angeles').startOf('day');

    const shippingDate = today.plus({ days: daysToAdd });
    return shippingDate.toISODate();
  }

  // Match MMDD
  const mmddMatch = datePart.match(/^(\d{2})(\d{2})$/);
  if (mmddMatch) {
    const month = Number(mmddMatch[1]);
    const day = Number(mmddMatch[2]);
    const now = DateTime.now().setZone('America/Los_Angeles').startOf('day');

    let candidate = DateTime.fromObject(
      { year: now.year, month, day },
      { zone: 'America/Los_Angeles' }
    );

    if (candidate <= now) {
      candidate = now.plus({ days: 28 });
    }

    return candidate.toISODate();
  }

  return null;
}

export function isYear(component: string) {
  return /^\d{4}-\d{4}$/.test(component) || /^\d{4}$/.test(component);
}

export function detectYearRange(input: string) {
  // Regular expression to match a year range in the format YYYY-YYYY
  const yearRangePattern = /\b\d{4}-\d{4}\b/;
  const match = input.match(yearRangePattern);

  // Return the matched year range if found, otherwise return null
  return match ? match[0] : null;
}

/**
 * Extracts year range from a product handle string.
 * Returns a tuple of [startYear, endYear] if found, null if no valid range found.
 *
 * @example
 * extractYearRange("acura-2013-2022-ilx-car-cover-bkgr") // returns [2013, 2022]
 * extractYearRange("ac-1979-1985-3000me-car-cover-bkgr") // returns [1979, 1985]
 * extractYearRange("ac-shelby-cobra-car-cover-bkgr") // returns null
 */
export function extractYearRange(handle: string): [number, number] | null {
  // Pattern to match two 4-digit numbers between 1900-2100 separated by a hyphen
  const pattern = /(\d{4})-(\d{4})/;

  const match = handle.match(pattern);
  if (!match) {
    return null;
  }

  const startYear = parseInt(match[1]);
  const endYear = parseInt(match[2]);

  // Validate years are in valid range
  if (
    startYear >= 1900 &&
    startYear <= 2100 &&
    endYear >= 1900 &&
    endYear <= 2100 &&
    startYear <= endYear
  ) {
    return [startYear, endYear];
  }

  return null;
}

export function isValidYear(year: string): boolean {
  const yearNum = parseInt(year);
  return !isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2100;
}

// Helper: Resolve year generation from year and submodel1
export function resolveYearGeneration(
  year: string,
  currentSelections: Partial<ProductData>,
  additionalConditions: Partial<ProductData> = {},
  modelData: IProductData[]
): string | null {
  const allConditions = { ...currentSelections, ...additionalConditions };

  // Filter out `year` explicitly to avoid conflicts
  const { year: _, ...filteredConditions } = allConditions;

  const isYearInRange = (year: string, yearGeneration: string): boolean => {
    if (yearGeneration.includes('-')) {
      const [start, end] = yearGeneration.split('-').map(Number);
      return Number(year) >= start && Number(year) <= end;
    }
    return yearGeneration === year;
  };

  const matchesConditions = (product: ProductData): boolean => {
    return Object.entries(filteredConditions).every(([key, value]) => {
      if (value == null) return true; // Skip null/undefined values
      const productValue = product[key as keyof ProductData];

      if (key === 'display_set') {
        return normalizeDisplaySet(productValue || '') === normalizeDisplaySet(value || '');
      }

      if (typeof productValue === 'string' && typeof value === 'string') {
        return productValue.toLowerCase() === value.toLowerCase();
      }

      return productValue === value;
    });
  };

  const matchingProduct = modelData.find((product) => {
    const yearInRange = isYearInRange(year, product.year_generation || '');
    const conditionsMatch = matchesConditions(product);

    if (!yearInRange) {
      console.log(
        `Product does not match year: ${product.year_generation}, selected year: ${year}`
      );
    }
    if (!conditionsMatch) {
      console.log(`Product does not match conditions:`, product, `Expected:`, filteredConditions);
    }

    return yearInRange && conditionsMatch;
  });

  if (!matchingProduct) {
    console.log('No matching product found for:', {
      year,
      filteredConditions,
    });
  }

  return matchingProduct ? matchingProduct.year_generation : null;
}

// Helper: Check if a year is in a year generation range
export function yearInYearGeneration(year: string, year_generation: string) {
  if (!year || !year_generation) return false; // Guard clause for invalid inputs
  if (year_generation.includes('-')) {
    const [start, end] = year_generation.split('-').map(Number);
    if (isNaN(start) || isNaN(end)) return false; // Handle invalid range
    return Number(year) >= start && Number(year) <= end;
  }
  return year_generation === year;
}

/**
 * Adjusts preorder date if it's in the past by adding 4 weeks.
 * If 4 weeks from the original date is still in the past, adds 4 weeks from today.
 *
 * @param expectedInHouseDate - Date string in YYYY-MM-DD format
 * @returns Adjusted date string in YYYY-MM-DD format, or null if input is invalid
 */
export function adjustPreorderDate(expectedInHouseDate: string | null): string | null {
  if (!expectedInHouseDate) return null;

  const today = DateTime.now().setZone('America/Los_Angeles').startOf('day');
  const originalDate = DateTime.fromFormat(expectedInHouseDate, 'yyyy-MM-dd', {
    zone: 'America/Los_Angeles',
  });

  if (!originalDate.isValid) return expectedInHouseDate; // Return original if parsing fails

  // If the original date is not in the past, return it unchanged
  if (originalDate >= today) {
    return expectedInHouseDate;
  }

  // Add 4 weeks to the original date ===> Add 4 weeks to Today's date
  const dateWith4Weeks = today.plus({ weeks: 4 });

  // Return the original date + 4 weeks
  return dateWith4Weeks.toISODate(); // Returns YYYY-MM-DD format
}
